import React, { useState, useContext, useEffect, useMemo, useRef, useCallback } from 'react';
import { Box, Flex, Text, Spinner } from '@chakra-ui/react';
import { FiCalendar, FiCheckSquare, FiMail, FiArchive, FiAlertCircle } from 'react-icons/fi';
import HelpModal from '../../../components/HelpModal';
import { useNavigate } from 'react-router-dom';
import { Collab, Campaign, MarketplaceOrderStatus, MarketplaceOrderListItemDto } from '@foodfluence/shared';
import { CollabDataContext, CollabDataLoadingContext } from '../../../contexts/collabDataContext';
import { InfluencerDataContext } from '../../../contexts/InfluencerDataContext';
import { InfluencerCampaignDataContext } from '../../../contexts/InfluencerCampaignDataContext';
import { ClientDataContext } from '../../../contexts/ClientDataContext';
import { getClientIdFromCollabRequest } from '../../../utils/collabUtils';
import { collabApi } from '../../../services/api/collab';
import { useMarketplaceOrders } from '../../../contexts/MarketplaceOrdersContext';
import CollabCard, { HomeRow, RowAvatar, RowPill, RowPillTone } from '../components/home/CollabCard';
import PastCollabCard from '../components/home/PastCollabCard';
import TaskCard from '../components/home/TaskCard';
import HomeSection from '../components/home/HomeSection';
import DashboardCard from '../components/home/DashboardCard';
import PromoBanner from '../components/home/PromoBanner';
import UgcPromoBanner from '../components/home/UgcPromoBanner';
import LocalCreatorsPromoBanner from '../components/home/LocalCreatorsPromoBanner';
import {
  getCurrentCollabs,
  getSchedulingRequiredCollabs,
  getPreviousTabSections,
  getWaitlistCTA,
  getExtensionCTA,
  showSpotsBadge,
  getSpotsCTA,
  isCampaignFull,
  collabCanBeExpired,
  CollabTask,
  CardCta,
} from '../utils/collabUtils';
import { needsExtensionRequest } from 'utils/collabUtils';
import {
  getAllTasks,
  getTasksTabSections,
} from '../utils/collabTaskUtils';
import { getInvitesTabSections } from '../utils/collabUtils';
import { marketplaceOrderIsCanceled } from '../utils/marketplaceOrderUtils';
import { getNewCount, setTabSeenIds } from '../utils/tabSeenUtils';
import { resolveDueDateTimezone } from 'utils/timezoneUtils';
import { ff } from '../../../theme';
import { DisplayHeading, UnderlineTabs } from '../../../components/ui';

const TABS = ['All', 'Tasks', 'Pending', 'Orders', 'Previous'] as const;
type TabName = typeof TABS[number];

const TAB_STORAGE_KEY = 'homepage_active_tab';

// Module-level cache so spots persist when navigating away and back
let cachedSpotsMap: Record<string, number> = {};

type MarketplaceOrderListItem = MarketplaceOrderListItemDto;

function orderSubtitle(order: MarketplaceOrderListItem): string | undefined {
  const brand = order.brandName?.trim();
  if (brand) return brand;
  const project = order.orderBrief?.projectName?.trim();
  if (project) return project;
  return 'Marketplace Order';
}

function statusLabel(status: MarketplaceOrderStatus): string {
  if (status === 'pendingCreatorAcceptance') return 'Pending acceptance';
  if (status === 'creatorCountered') return 'Counter pending';
  if (status === 'inviteExpired') return 'Invite expired';
  if (status === 'pendingCompletion') return 'In progress';
  if (status === 'creatorRejected') return 'Declined';
  if (status === 'adminCanceled') return 'Canceled';
  return 'Completed';
}

function statusTone(status: MarketplaceOrderStatus): RowPillTone {
  if (status === 'pendingCreatorAcceptance') return 'red';
  if (status === 'pendingCompletion') return 'green';
  return 'grey';
}

/** Accept/counter/expired detail routes; in-progress and terminal orders use submission page. */
function marketplaceOrdersListPath(o: MarketplaceOrderListItem): string {
  const submissionStatuses: MarketplaceOrderStatus[] = [
    'pendingCompletion',
    'completed',
    'creatorRejected',
    'adminCanceled',
  ];
  if (submissionStatuses.includes(o.status)) {
    return `/collabs/marketplace-orders/${o.id}/submission`;
  }
  return `/collabs/marketplace-orders/${o.id}`;
}

const getStoredTab = (): TabName => {
  const stored = sessionStorage.getItem(TAB_STORAGE_KEY);
  if (stored && (TABS as readonly string[]).includes(stored)) {
    return stored as TabName;
  }
  return 'All';
};

interface HomePageProps {
  isFirstTime?: boolean;
}

const HomePage: React.FC<HomePageProps> = ({ isFirstTime = false }) => {
  const navigate = useNavigate();
  const { loading: influencerLoading, influencerData } = useContext(InfluencerDataContext);
  const collabData = useContext(CollabDataContext);
  const collabDataLoading = useContext(CollabDataLoadingContext);
  const campaignData = useContext(InfluencerCampaignDataContext);
  const clients = useContext(ClientDataContext);

  const isPageLoading = influencerLoading || collabDataLoading;
  const [activeTab, setActiveTab] = useState<TabName>(getStoredTab);
  const hasMarketplaceAccess = influencerData?.hasMarketplaceAccess === true;
  const visibleTabs = useMemo(
    () => (hasMarketplaceAccess ? [...TABS] : (TABS.filter((t) => t !== 'Orders') as TabName[])),
    [hasMarketplaceAccess]
  );
  const [spotsMap, setSpotsMap] = useState<Record<string, number>>(() => ({ ...cachedSpotsMap }));
  const [badgeInvalidator, setBadgeInvalidator] = useState(0);
  const [isHelpModalOpen, setIsHelpModalOpen] = useState(isFirstTime);
  const [, setMinuteTick] = useState(() => Date.now());
  const { orders: marketplaceOrdersRaw, loading: ordersLoading, error: ordersError, refetch: refetchMarketplaceOrders } = useMarketplaceOrders();
  const marketplaceOrders = useMemo(() => {
    const rows = [...marketplaceOrdersRaw];
    rows.sort((a, b) => (b.createdAt?.seconds ?? 0) - (a.createdAt?.seconds ?? 0));
    return rows;
  }, [marketplaceOrdersRaw]);

  useEffect(() => {
    if (collabData.length === 0) {
      return;
    }
    const fetchSpots = async () => {
      // Show cached data immediately (persists across navigations)
      if (Object.keys(cachedSpotsMap).length > 0) {
        setSpotsMap({ ...cachedSpotsMap });
      }
      const map: Record<string, number> = { ...cachedSpotsMap };
      const seen = new Set<string>();
      for (const collab of collabData) {
        const campaign = campaignData[collab.id];
        if (!campaign || !collab.influencerCampaignID) continue;
        if (seen.has(collab.influencerCampaignID)) continue;
        seen.add(collab.influencerCampaignID);
        try {
          const res = await collabApi.getAvailableSpots(collab.influencerCampaignID, collab.id);
          map[collab.influencerCampaignID] = res.availableSpots;
        } catch {
          console.error('Error fetching remaining spots:');
        }
      }
      cachedSpotsMap = map;
      setSpotsMap(map);
    };
    fetchSpots();
  }, [collabData, campaignData]);

  useEffect(() => {
    const interval = setInterval(() => {
      setMinuteTick(Date.now());
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (activeTab === 'Orders' && hasMarketplaceAccess) {
      void refetchMarketplaceOrders();
    }
  }, [activeTab, hasMarketplaceAccess, refetchMarketplaceOrders]);

  const navigateToCollab = (collab: Collab) => {
    const campaign = campaignData[collab.id];
    const client = campaign ? getClient(collab, campaign) : clients[collab.clientID!];
    const spots = collab.influencerCampaignID ? spotsMap[collab.influencerCampaignID] ?? null : null;
    const isWaitlist = campaign && spots !== null && isCampaignFull(campaign, spots, collab);
    const isExtension = campaign && needsExtensionRequest(campaign, collab);

    if (isWaitlist || isExtension) {
      navigate(`/collabs/collab-details/${collab.id}`);
    } else if (collab.status === 'pendingInfluencerApproval') {
      navigate(`/collabs/review-collab/${collab.id}`);
    } else if (collab.status === 'pendingClientApproval') {
      navigate(`/collabs/request-collab`, { state: { requestedCollab: collab, campaign: campaign ?? null, client } });
    } else {
      navigate(`/collabs/collab-details/${collab.id}`);
    }
  };

  const getClient = (collab: Collab, campaign: Campaign) => {
    return clients[getClientIdFromCollabRequest(collab, campaign)];
  };

  const getInviteExpirySubtitle = (collab: Collab): string | undefined => {
    const offerExpiry = collabCanBeExpired(collab);
    if (!offerExpiry) return undefined;
    const totalMs = offerExpiry.getTime() - Date.now();
    const totalMinutes = Math.floor(totalMs / 60000);
    const days = Math.floor(totalMinutes / (60 * 24));
    const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
    const minutes = totalMinutes % 60;
    return `${days}D ${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  };

  const renderCollabCard = (collab: Collab, baseCta: CardCta, timeLeft?: string | null, subtitleOverride?: string) => {
    const campaign = campaignData[collab.id];
    const client = campaign ? getClient(collab, campaign) : clients[collab.clientID!];
    const subtitle = subtitleOverride ?? (!campaign ? 'Custom Collab Request' : undefined);
    const spots = collab.influencerCampaignID ? spotsMap[collab.influencerCampaignID] ?? null : null;
    const isExtension = campaign ? needsExtensionRequest(campaign, collab) : false;
    const cta = campaign && showSpotsBadge(collab, campaign, spots) && spots != null && !isExtension ? getSpotsCTA(spots) : baseCta;
    return (
      <CollabCard
        key={collab.id}
        collab={collab}
        campaign={campaign}
        client={client}
        cta={cta}
        subtitle={subtitle}
        timeLeft={timeLeft}
        onClick={() => navigateToCollab(collab)}
      />
    );
  };

  const renderPastCollabCard = (collab: Collab) => {
    const campaign = campaignData[collab.id];
    if (!campaign) return null;
    return (
      <PastCollabCard
        key={collab.id}
        collab={collab}
        campaign={campaign}
        client={getClient(collab, campaign)}
        cta={{ label: '', variant: 'arrow' }}
      />
    );
  };

  const renderTaskCard = (task: CollabTask, idx: number) => {
    const campaign = campaignData[task.collab.id];
    if (!campaign) return null;
    const spots = task.collab.influencerCampaignID ? spotsMap[task.collab.influencerCampaignID] ?? null : null;
    return (
      <TaskCard
        key={`${task.collab.id}-${task.title}-${idx}`}
        task={task}
        campaign={campaign}
        client={getClient(task.collab, campaign)}
        remainingSpots={spots}
        onClick={() => navigateToCollab(task.collab)}
      />
    );
  };

  const renderEmptyState = (icon: React.ReactNode, title: string, subtitle: string) => (
    <Box px="20px">
      <Flex mt="48px" p="32px 24px" borderRadius="24px" bg={ff.grey} direction="column" align="center" gap="10px" textAlign="center">
        <Flex w="56px" h="56px" borderRadius="full" bg={ff.ink} color={ff.lime} align="center" justify="center">
          {icon}
        </Flex>
        <Text textStyle="display" fontSize="30px" lineHeight={1.12}>{title}</Text>
        <Text maxW="260px" fontSize="14px" lineHeight="1.4" color="#55555A">{subtitle}</Text>
      </Flex>
    </Box>
  );

  const renderLoading = (label: string) => (
    <Flex flex={1} minH="200px" align="center" justify="center" direction="column" gap="12px">
      <Spinner size="lg" color={ff.ink} thickness="3px" />
      <Text fontSize="14px" color={ff.text2}>{label}</Text>
    </Flex>
  );

  // ---- Tasks Tab ----
  const tasksSections = getTasksTabSections(
    collabData,
    campaignData,
    spotsMap,
    // ponytail: creator zone only; the campaign-location step needs a fetch per campaign, add it if
    // creators without a stored zone show up with located ship-to-home campaigns.
    resolveDueDateTimezone(influencerData?.timezone)
  );
  const { dueSoon, contentSubmission: submittingContentTasksFiltered, scheduling: submitDetailsToConfirmTasksFiltered, redemption: redemptionTasksFiltered, rewards: rewardTasksFiltered } = tasksSections;
  const allTasks = getAllTasks(collabData, campaignData);

  // ---- All Tab ----
  const allCurrentCollabs = getCurrentCollabs(collabData, campaignData);
  const schedulingRequiredCollabs = getSchedulingRequiredCollabs(collabData, campaignData);
  const previousSections = getPreviousTabSections(collabData);
  const allPreviousCollabs = [...previousSections.completed, ...previousSections.rejected, ...previousSections.canceled, ...previousSections.expired];

  // ---- Invites Tab ----
  const invitesSections = getInvitesTabSections(collabData, campaignData, spotsMap);
  const allInvites = [
    ...invitesSections.counters,
    ...invitesSections.collabRequests,
    ...invitesSections.sentRequests,
    ...invitesSections.waitlist,
    ...invitesSections.extensions,
  ];

  // ---- Previous Tab ----
  const { completed: completedCollabs, rejected: rejectedCollabs, canceled: canceledCollabs, expired: expiredCollabs } = previousSections;

  // ---- Tab badge counts (unviewed items) ----
  const tabItemIds = useMemo(
    () => ({
      All: [...allCurrentCollabs, ...allInvites, ...allPreviousCollabs].map((c) => c.id),
      Tasks: allTasks.map((t) => `${t.collab.id}::${t.title}`),
      Pending: allInvites.map((c) => c.id),
      Orders: marketplaceOrders.map((o) => o.id),
      Previous: allPreviousCollabs.map((c) => c.id),
    }),
    [allCurrentCollabs, allInvites, allPreviousCollabs, allTasks, marketplaceOrders]
  );

  const tabBadgeCounts = useMemo(
    () =>
      (visibleTabs as readonly TabName[]).reduce(
        (acc, tab) => {
          if (tab === 'Tasks') {
            acc[tab] = allTasks.length;
          } else if (tab === 'Pending') {
            acc[tab] = allInvites.length;
          } else if (tab === 'Orders') {
            acc[tab] = marketplaceOrders.length;
          } else {
            acc[tab] = getNewCount(tabItemIds[tab], tab);
          }
          return acc;
        },
        {} as Record<TabName, number>
      ),
    [tabItemIds, badgeInvalidator, allTasks, allInvites, visibleTabs, marketplaceOrders.length]
  );

  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const handleScroll = useCallback(() => {
    if (scrollContainerRef.current) {
      sessionStorage.setItem(`homepage_scroll_${activeTab}`, String(scrollContainerRef.current.scrollTop));
    }
  }, [activeTab]);

  // Restore scroll position when loading completes or tab changes
  useEffect(() => {
    if (isPageLoading) return;
    const saved = sessionStorage.getItem(`homepage_scroll_${activeTab}`);
    if (saved && scrollContainerRef.current) {
      requestAnimationFrame(() => {
        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollTop = parseInt(saved, 10);
        }
      });
    }
  }, [isPageLoading, activeTab]);

  const handleTabClick = (tab: TabName) => {
    setActiveTab(tab);
    sessionStorage.setItem(TAB_STORAGE_KEY, tab);
  };

  useEffect(() => {
    setTabSeenIds(activeTab, tabItemIds[activeTab]);
    setBadgeInvalidator((v) => v + 1);
  }, [activeTab, tabItemIds]);

  useEffect(() => {
    if (!hasMarketplaceAccess && activeTab === 'Orders') {
      setActiveTab('All');
      sessionStorage.setItem(TAB_STORAGE_KEY, 'All');
    }
  }, [activeTab, hasMarketplaceAccess]);

  const renderAllTab = () => {
    const isEmpty =
      allCurrentCollabs.length === 0 &&
      allInvites.length === 0 &&
      allPreviousCollabs.length === 0;

    const showUgcBanner = influencerData?.showCreatePortfolioBanner == true || influencerData?.showCreatePortfolioBanner == undefined;
    const showLcBanner = hasMarketplaceAccess && influencerData?.showLocalCreatorsBanner !== false;

    // Each banner hides itself once dismissed; the row collapses when all are gone.
    const banners = (
      <Flex
        gap="10px"
        overflowX="auto"
        p="18px 20px 0 20px"
        sx={{ scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}
        _empty={{ display: 'none' }}
      >
        <PromoBanner />
        {showUgcBanner && <UgcPromoBanner />}
        {showLcBanner && <LocalCreatorsPromoBanner />}
      </Flex>
    );

    if (isEmpty) {
      return (
        <>
          {banners}
          {renderEmptyState(
            <FiCalendar size={24} />,
            'No collaborations yet',
            'Apply to a campaign and your collabs will show up here.'
          )}
        </>
      );
    }

    return (
      <>
        {banners}
        <HomeSection title="Current Collabs" itemCount={allCurrentCollabs.length}>
          {allCurrentCollabs.map(c => renderCollabCard(c, { label: '', variant: 'arrow' }))}
        </HomeSection>
        <HomeSection title="Scheduling Required" itemCount={schedulingRequiredCollabs.length}>
          {schedulingRequiredCollabs.map(c => renderCollabCard(c, { label: '', variant: 'arrow' }))}
        </HomeSection>

        <HomeSection title="Counters" itemCount={invitesSections.counters.length}>
          {invitesSections.counters.map(c => renderCollabCard(c, { label: '', variant: 'arrow' }))}
        </HomeSection>
        <HomeSection title="Invites" itemCount={invitesSections.collabRequests.length}>
          {invitesSections.collabRequests.map(c =>
            renderCollabCard(c, { label: '', variant: 'arrow' }, getInviteExpirySubtitle(c))
          )}
        </HomeSection>
        <HomeSection title="Requested Collabs" itemCount={invitesSections.sentRequests.length}>
          {invitesSections.sentRequests.map(c => renderCollabCard(c, { label: '', variant: 'arrow' }))}
        </HomeSection>
        <HomeSection title="Waitlist" itemCount={invitesSections.waitlist.length}>
          {invitesSections.waitlist.map(c => renderCollabCard(c, getWaitlistCTA(c)))}
        </HomeSection>
        <HomeSection title="Expired" itemCount={invitesSections.extensions.length}>
          {invitesSections.extensions.map(c => renderCollabCard(c, getExtensionCTA(c, campaignData[c.id])))}
        </HomeSection>

        <HomeSection title="Previous Collabs" itemCount={allPreviousCollabs.length}>
          {allPreviousCollabs.map(c => renderPastCollabCard(c))}
        </HomeSection>
      </>
    );
  };

  const renderTasksTab = () => (
    <>
      {allTasks.length === 0 ? (
        renderEmptyState(
          <FiCheckSquare size={24} />,
          'No tasks right now',
          'You are all caught up. Anything that needs you next will land here.'
        )
      ) : (
        <>
          <HomeSection title="Due Soon" itemCount={dueSoon.length} collapsible highlight storageKey="tasks_due_soon">
            {dueSoon.map((t, i) => renderTaskCard(t, i))}
          </HomeSection>
          <HomeSection title="Content Submission Required" itemCount={submittingContentTasksFiltered.length} collapsible storageKey="tasks_content_submission">
            {submittingContentTasksFiltered.map((t, i) => renderTaskCard(t, i))}
          </HomeSection>
          <HomeSection title="Scheduling Required" itemCount={submitDetailsToConfirmTasksFiltered.length} collapsible storageKey="tasks_scheduling">
            {submitDetailsToConfirmTasksFiltered.map((t, i) => renderTaskCard(t, i))}
          </HomeSection>
          <HomeSection title="Redemption Required" itemCount={redemptionTasksFiltered.length} collapsible storageKey="tasks_redemption">
            {redemptionTasksFiltered.map((t, i) => renderTaskCard(t, i))}
          </HomeSection>
          <HomeSection title="Rewards" itemCount={rewardTasksFiltered.length} collapsible storageKey="tasks_rewards">
            {rewardTasksFiltered.map((t, i) => renderTaskCard(t, i))}
          </HomeSection>
        </>
      )}
    </>
  );

  const renderInvitesTab = () => {
    if (allInvites.length === 0) {
      return renderEmptyState(
        <FiMail size={24} />,
        'No pending collabs',
        'Requests, invites and counters waiting on a reply show up here.'
      );
    }

    return (
      <>
        <HomeSection title="Counters" itemCount={invitesSections.counters.length}>
          {invitesSections.counters.map(c => renderCollabCard(c, { label: '', variant: 'arrow' }))}
        </HomeSection>
        <HomeSection title="Invites" itemCount={invitesSections.collabRequests.length}>
          {invitesSections.collabRequests.map(c =>
            renderCollabCard(c, { label: '', variant: 'arrow' }, getInviteExpirySubtitle(c))
          )}
        </HomeSection>
        <HomeSection title="Requested Collabs" itemCount={invitesSections.sentRequests.length}>
          {invitesSections.sentRequests.map(c => renderCollabCard(c, { label: '', variant: 'arrow' }))}
        </HomeSection>
        <HomeSection title="Waitlist" itemCount={invitesSections.waitlist.length}>
          {invitesSections.waitlist.map(c => renderCollabCard(c, getWaitlistCTA(c)))}
        </HomeSection>
        <HomeSection title="Expired" itemCount={invitesSections.extensions.length}>
          {invitesSections.extensions.map(c => renderCollabCard(c, getExtensionCTA(c, campaignData[c.id])))}
        </HomeSection>
      </>
    );
  };

  const renderPreviousTab = () => {
    if (allPreviousCollabs.length === 0) {
      return renderEmptyState(
        <FiArchive size={24} />,
        'No previous collabs',
        'Completed, rejected, canceled and expired collabs are kept here.'
      );
    }

    return (
      <>
        <HomeSection title="Completed" itemCount={completedCollabs.length} collapsible storageKey="previous_completed">
          {completedCollabs.map(c => renderPastCollabCard(c))}
        </HomeSection>
        <HomeSection title="Rejected" itemCount={rejectedCollabs.length} collapsible storageKey="previous_rejected">
          {rejectedCollabs.map(c => renderPastCollabCard(c))}
        </HomeSection>
        <HomeSection title="Canceled" itemCount={canceledCollabs.length} collapsible storageKey="previous_canceled">
          {canceledCollabs.map(c => renderPastCollabCard(c))}
        </HomeSection>
        <HomeSection title="Expired" itemCount={expiredCollabs.length} collapsible storageKey="previous_expired">
          {expiredCollabs.map(c => renderPastCollabCard(c))}
        </HomeSection>
      </>
    );
  };

  const renderOrdersTab = () => {
    if (ordersLoading) {
      return renderLoading('Loading orders...');
    }

    if (ordersError) {
      return renderEmptyState(
        <FiAlertCircle size={24} />,
        'Could not load orders',
        ordersError
      );
    }

    if (marketplaceOrders.length === 0) {
      return renderEmptyState(
        <FiArchive size={24} />,
        'No marketplace orders yet',
        'Marketplace orders you receive will show up here.'
      );
    }

    const pendingAcceptance = marketplaceOrders.filter((o) => o.status === 'pendingCreatorAcceptance');
    const negotiating = marketplaceOrders.filter((o) => o.status === 'creatorCountered');
    const inviteExpired = marketplaceOrders.filter((o) => o.status === 'inviteExpired');
    const pendingCompletion = marketplaceOrders.filter((o) => o.status === 'pendingCompletion');
    const completed = marketplaceOrders.filter((o) => o.status === 'completed' || o.status === 'creatorRejected');
    const canceled = marketplaceOrders.filter((o) => marketplaceOrderIsCanceled(o.status));

    const renderOrderRow = (o: MarketplaceOrderListItem) => (
      <HomeRow
        key={o.id}
        onClick={() => navigate(marketplaceOrdersListPath(o))}
        avatar={<RowAvatar name="ORD" />}
        primary={orderSubtitle(o)}
        secondary={o.id}
        right={<RowPill tone={statusTone(o.status)}>{statusLabel(o.status)}</RowPill>}
      />
    );

    return (
      <>
        <HomeSection title="Pending Acceptance" itemCount={pendingAcceptance.length} collapsible highlight storageKey="orders_pending_acceptance">
          {pendingAcceptance.map(renderOrderRow)}
        </HomeSection>
        <HomeSection title="Negotiating" itemCount={negotiating.length} collapsible storageKey="orders_negotiating">
          {negotiating.map(renderOrderRow)}
        </HomeSection>
        <HomeSection title="Invite expired" itemCount={inviteExpired.length} collapsible storageKey="orders_invite_expired">
          {inviteExpired.map(renderOrderRow)}
        </HomeSection>
        <HomeSection title="In Progress" itemCount={pendingCompletion.length} collapsible storageKey="orders_in_progress">
          {pendingCompletion.map(renderOrderRow)}
        </HomeSection>
        <HomeSection title="Completed" itemCount={completed.length} collapsible storageKey="orders_completed">
          {completed.map(renderOrderRow)}
        </HomeSection>
        <HomeSection title="Canceled" itemCount={canceled.length} collapsible storageKey="orders_canceled">
          {canceled.map(renderOrderRow)}
        </HomeSection>
      </>
    );
  };

  const taskCount = allTasks.length;

  return (
    <Box bg="white" minH="100svh" display="flex" flexDirection="column" fontFamily="display" color={ff.ink} lineHeight="normal">
      <HelpModal isOpen={isHelpModalOpen} onClose={() => setIsHelpModalOpen(false)} />
      <Flex direction="column" gap="18px" p="24px 20px 0 20px" flexShrink={0}>
        <Flex align="flex-end" justify="space-between" gap="12px">
          <DisplayHeading>Home</DisplayHeading>
          <Box
            as="button"
            type="button"
            h="44px"
            display="flex"
            alignItems="center"
            flex="none"
            onClick={() => handleTabClick('Tasks')}
          >
            <Flex as="span" h="28px" px="10px" borderRadius="full" bg={ff.ink} color={ff.lime} align="center" fontSize="13px" fontWeight={600}>
              {taskCount ? `${taskCount} ${taskCount === 1 ? 'task' : 'tasks'} due` : 'All caught up'}
            </Flex>
          </Box>
        </Flex>
        <DashboardCard variant={activeTab === 'Orders' ? 'marketplaceOrders' : 'default'} />
      </Flex>

      <Box position="sticky" top={0} zIndex={10} bg="white" mt="10px" px="20px">
        <UnderlineTabs
          gap="18px"
          h="48px"
          active={activeTab}
          onChange={(key) => handleTabClick(key as TabName)}
          tabs={visibleTabs.map((tab) => {
            const n = tabBadgeCounts[tab];
            return {
              key: tab,
              label: tab,
              badge: n > 0 && tab !== 'Previous' ? (n > 99 ? '99+' : n) : undefined,
            };
          })}
        />
      </Box>

      {/* Tab content */}
      <Box ref={scrollContainerRef} flex={1} overflowY="auto" pt="4px" pb="24px" onScroll={handleScroll}>
        {isPageLoading ? (
          renderLoading('Loading collabs...')
        ) : (
          <>
            {activeTab === 'All' && renderAllTab()}
            {activeTab === 'Tasks' && renderTasksTab()}
            {activeTab === 'Pending' && renderInvitesTab()}
            {activeTab === 'Orders' && hasMarketplaceAccess && renderOrdersTab()}
            {activeTab === 'Previous' && renderPreviousTab()}
          </>
        )}
      </Box>
    </Box>
  );
};

export default HomePage;
